import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RegisterItemList } from '../RegisterItemList';
import type { Item } from '../RegisterItemList';
import { peopleApi } from '../../services/api';
import type { Person, DataField } from '../../types/models';
import { VENDOR_ROLES, VENDOR_ROLE_LABELS, VENDOR_ROLE_TONE, toVendorRoles } from './SubprocessorDetail';
import type { VendorRole } from './SubprocessorDetail';

type SubprocessorStatus = 'Draft' | 'Active' | 'Terminated';

const STATUS_TONE: Record<SubprocessorStatus, string> = {
  Draft: 'neutral',
  Active: 'success',
  Terminated: 'dark',
};

const TRI_STATE_TONE: Record<string, string> = {
  Yes: 'success',
  No: 'danger',
  'Not applicable': 'neutral',
};

const TRANSFER_MECHANISM_LABELS: Record<string, string> = {
  adequacy: 'Adequacy decision',
  sccs: 'SCCs',
  bcrs: 'BCRs',
  derogation: 'Derogation',
  none: 'No mechanism',
};

const TRANSFER_MECHANISM_TONE: Record<string, string> = {
  adequacy: 'success',
  sccs: 'success',
  bcrs: 'success',
  derogation: 'warning',
  none: 'danger',
};

const Dash: React.FC = () => <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>;

const statusChip = (status: string): React.ReactNode => {
  const key = status as SubprocessorStatus;
  const tone = STATUS_TONE[key] || 'neutral';
  return <span className={`np-chip np-tone-${tone}`}>{status || 'Draft'}</span>;
};

const fieldsOf = (item: Item): DataField[] =>
  Array.isArray(item.dataFields) ? (item.dataFields as DataField[]) : [];

const hasSpecialCategory = (item: Item): boolean =>
  fieldsOf(item).some((f) => f.isSpecialCategory);

/** A recorded transfer outside the EEA with no lawful Chapter V mechanism - the same
 * reportable state Processing Activities tracks, applied here to a vendor relationship. */
const hasUnlawfulTransfer = (item: Item): boolean =>
  Boolean(item.transfersOutsideEea) && (String(item.transferMechanism || 'none') === 'none');

const isDpaMissing = (item: Item): boolean => String(item.dpaInPlace || '') !== 'Yes';

/** A fixed-term contract ending within 60 days, or a duration nobody has ever recorded. */
const needsContractReview = (item: Item): boolean => {
  const duration = String(item.contractDuration || 'Unknown');
  if (duration === 'Unknown') return true;
  const end = String(item.contractEndDate || '');
  if (duration === 'Fixed term' && end) {
    const days = (new Date(end).getTime() - Date.now()) / 86400000;
    return days <= 60;
  }
  return duration === 'Fixed term' && !end;
};

export const SubprocessorsList: React.FC = () => {
  const [vendorRoleFilter, setVendorRoleFilter] = useState<string>('');
  const [dpaFilter, setDpaFilter] = useState<string>('');

  // Subprocessors store only an ownerId; the People register owns the display name -
  // matching Processing Activities' own convention for the same field.
  const { data: people = [] } = useQuery<Person[]>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
  });

  const peopleById = useMemo(() => {
    const map = new Map<string, string>();
    people.forEach((p) => map.set(p.personId, p.personName));
    return map;
  }, [people]);

  const ownerLabel = (item: Item): string => {
    const id = String(item.ownerId || '');
    if (!id) return '';
    return peopleById.get(id) || id;
  };

  return (
    <RegisterItemList
      title="Controllers & Processors"
      iconClass="ri-shield-keyhole-line"
      listPath="/subprocessors"
      detailPathPrefix="/subprocessors"
      idField="subprocessorId"
      titleField="name"
      statusField="status"
      statusChip={statusChip}
      statCards={(items) => [
        {
          label: 'Total records',
          value: items.length,
          icon: 'ri-building-4-line',
          tone: 'primary',
        },
        {
          label: 'Missing DPA',
          value: items.filter(isDpaMissing).length,
          icon: 'ri-file-warning-line',
          tone: 'danger',
        },
        {
          label: 'Unlawful transfers',
          value: items.filter(hasUnlawfulTransfer).length,
          icon: 'ri-global-line',
          tone: 'danger',
        },
        {
          label: 'Contract review needed',
          value: items.filter(needsContractReview).length,
          icon: 'ri-calendar-close-line',
          tone: 'warning',
        },
      ]}
      extraFilters={[
        {
          id: 'vendor-role',
          control: (
            <select
              className="np-select"
              style={{ width: 'auto', maxWidth: 200 }}
              value={vendorRoleFilter}
              onChange={(e) => setVendorRoleFilter(e.target.value)}
              aria-label="Filter by vendor role"
            >
              <option value="">Any vendor role</option>
              {VENDOR_ROLES.map((r) => <option key={r} value={r}>{VENDOR_ROLE_LABELS[r]}</option>)}
            </select>
          ),
          // A record can hold more than one role now - "includes" so a vendor tagged both
          // Processor and Controller shows up under either filter, not neither.
          predicate: (item) => !vendorRoleFilter || toVendorRoles(item.vendorRole).includes(vendorRoleFilter as VendorRole),
        },
        {
          id: 'dpa-status',
          control: (
            <select
              className="np-select"
              style={{ width: 'auto', maxWidth: 200 }}
              value={dpaFilter}
              onChange={(e) => setDpaFilter(e.target.value)}
              aria-label="Filter by DPA status"
            >
              <option value="">Any DPA status</option>
              <option value="Yes">DPA in place</option>
              <option value="No">DPA not in place</option>
              <option value="Not applicable">Not applicable</option>
            </select>
          ),
          predicate: (item) => !dpaFilter || String(item.dpaInPlace || 'Not applicable') === dpaFilter,
        },
      ]}
      extraColumns={[
        {
          header: 'Product',
          width: '160px',
          render: (item) => item.product ? <span className="np-sub">{String(item.product)}</span> : <Dash />,
          sortValue: (item) => String(item.product || ''),
        },
        {
          header: 'Vendor role',
          width: '220px',
          render: (item) => {
            const roles = toVendorRoles(item.vendorRole);
            if (roles.length === 0) return <Dash />;
            return (
              <span className="d-inline-flex flex-wrap gap-1">
                {roles.map((r) => (
                  <span key={r} className={`np-chip np-tone-${VENDOR_ROLE_TONE[r]}`}>{VENDOR_ROLE_LABELS[r]}</span>
                ))}
              </span>
            );
          },
          sortValue: (item) => toVendorRoles(item.vendorRole).map((r) => VENDOR_ROLE_LABELS[r]).join(', '),
        },
        {
          header: 'DPA',
          width: '140px',
          render: (item) => {
            const value = String(item.dpaInPlace || 'Not applicable');
            return <span className={`np-chip np-tone-${TRI_STATE_TONE[value] || 'neutral'}`}>{value}</span>;
          },
          sortValue: (item) => String(item.dpaInPlace || ''),
        },
        {
          header: 'Transfer',
          width: '160px',
          render: (item) => {
            const mechanism = String(item.transferMechanism || 'none');
            if (!item.transfersOutsideEea) return <Dash />;
            return (
              <span className={`np-chip np-tone-${TRANSFER_MECHANISM_TONE[mechanism] || 'neutral'}`}>
                {TRANSFER_MECHANISM_LABELS[mechanism] || mechanism}
              </span>
            );
          },
          sortValue: (item) => String(item.transferMechanism || ''),
        },
        {
          header: 'Data types',
          width: '130px',
          render: (item) => {
            const fields = fieldsOf(item);
            if (fields.length === 0) return <Dash />;
            const special = hasSpecialCategory(item);
            return (
              <span className="d-flex align-items-center gap-2">
                <span className="np-tabular">{fields.length}</span>
                {special && (
                  <span className="np-chip np-tone-danger" title="Includes special category (Article 9) data">
                    <i className="ri-alert-line"></i>
                  </span>
                )}
              </span>
            );
          },
          sortValue: (item) => fieldsOf(item).length,
        },
        {
          header: 'Owner',
          width: '160px',
          render: (item) => {
            const label = ownerLabel(item);
            return label ? <span className="np-sub">{label}</span> : <Dash />;
          },
          sortValue: (item) => ownerLabel(item),
        },
      ]}
    />
  );
};

export default SubprocessorsList;
